import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button } from './Primitives';

export type ToastTone = 'neutral' | 'ok' | 'warn' | 'crit';

export interface ToastUndo {
  label: string;
  onUndo: () => void;
  seconds: number;
}

export interface ToastInput {
  tone?: ToastTone;
  message: string;
  /** Only for reversible actions within 10 seconds. Never for money or production. */
  undo?: ToastUndo;
  /** Last six characters of a failed request's id. */
  requestId?: string | null;
}

interface ToastRecord extends ToastInput {
  id: number;
  expiresAt: number;
}

interface ToastApi {
  push: (toast: ToastInput) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi>({ push: () => undefined, dismiss: () => undefined });

const DEFAULT_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback((toast: ToastInput) => {
    const id = nextId.current;
    nextId.current += 1;
    const ttl = toast.undo ? toast.undo.seconds * 1000 : DEFAULT_MS;
    setToasts((current) => [...current, { ...toast, id, expiresAt: Date.now() + ttl }]);
  }, []);

  useEffect(() => {
    if (toasts.length === 0) return;
    const id = window.setInterval(() => {
      const now = Date.now();
      setToasts((current) => current.filter((toast) => toast.expiresAt > now));
    }, 500);
    return () => window.clearInterval(id);
  }, [toasts.length]);

  const api = useMemo<ToastApi>(() => ({ push, dismiss }), [dismiss, push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="o-toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`o-toast o-toast--${toast.tone ?? 'neutral'}`}>
            <span>{toast.message}</span>
            {toast.requestId ? <span className="o-muted">request {toast.requestId}</span> : null}
            {toast.undo ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  toast.undo?.onUndo();
                  dismiss(toast.id);
                }}
              >
                {toast.undo.label}
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => dismiss(toast.id)} aria-label="Dismiss">
              Dismiss
            </Button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(ToastContext);
}
